// Rating Utility Functions with Firebase Authentication
// File: src/utils/ratingUtils.js

import { getUserId, getUserIdToken, isSignedIn } from './authUtils'

const RATINGS_KEY = 'organlive_track_ratings'

/**
 * Get all ratings from localStorage (for migration/offline use)
 * @returns {Object} Object with trackId as key and rating as value
 */
export function getAllLocalRatings () {
  try {
    const stored = localStorage.getItem(RATINGS_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch (error) {
    console.error('Error reading local ratings:', error)
    return {}
  }
}

/**
 * Get rating for a specific track from localStorage
 * @param {string|number} trackId - The track ID
 * @returns {number} Rating (0-5) or 0 if not rated
 */
export function getLocalTrackRating (trackId) {
  if (!trackId) return 0
  const ratings = getAllLocalRatings()
  return ratings[String(trackId)] || 0
}

/**
 * Save rating to localStorage (backup/offline)
 * @param {string|number} trackId - The track ID
 * @param {number} rating - Rating value (1-5)
 * @returns {boolean} Success status
 */
export function saveLocalTrackRating (trackId, rating) {
  if (!trackId || rating < 1 || rating > 5) {
    console.error('Invalid trackId or rating value')
    return false
  }

  try {
    const ratings = getAllLocalRatings()
    ratings[String(trackId)] = rating
    localStorage.setItem(RATINGS_KEY, JSON.stringify(ratings))
    return true
  } catch (error) {
    console.error('Error saving local rating:', error)
    return false
  }
}

/**
 * Get user's IP address
 * @returns {Promise<string>} User's IP address
 */
async function getUserIP () {
  try {
    const response = await fetch('https://api.ipify.org?format=json')
    const data = await response.json()
    return data.ip
  } catch (error) {
    console.error('Error fetching IP:', error)
    return ''
  }
}

/**
 * Submit rating to Organlive API (with Firebase authentication)
 * @param {string|number} trackId - The track ID (work.id)
 * @param {number} rating - Rating value (1-5)
 * @returns {Promise<Object>} API response
 */
export async function submitRatingToAPI (trackId, rating) {
  if (!trackId || rating < 1 || rating > 5) {
    throw new Error('Invalid trackId or rating value')
  }

  // Check if user is signed in
  if (!isSignedIn()) {
    throw new Error('User must be signed in to rate tracks')
  }

  try {
    // Get Firebase user ID and token
    const userId = getUserId()
    const idToken = await getUserIdToken()

    if (!userId || !idToken) {
      throw new Error('Could not get user authentication')
    }

    // Get user IP (optional fallback)
    const userIP = await getUserIP()

    // Submit to API
    const url = 'https://api.organlive.com/ratings/submit'
    const body = {
      user_id: userId, // Firebase UID
      track_id: String(trackId), // work.id
      rating,
      ip: userIP // Optional backup identifier
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}` // Firebase ID token for verification
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `API returned ${response.status}: ${response.statusText}`)
    }

    const data = await response.json()
    return {
      success: true,
      data
    }
  } catch (error) {
    console.error('Error submitting rating to API:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

/**
 * Get user's ratings from API
 * @returns {Promise<Object>} Object with trackId as key and rating as value
 */
export async function getUserRatingsFromAPI () {
  if (!isSignedIn()) {
    return {}
  }

  try {
    const userId = getUserId()
    const idToken = await getUserIdToken()

    if (!userId || !idToken) {
      return {}
    }

    const url = `https://api.organlive.com/ratings/user/${userId}`
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    })

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`)
    }

    const data = await response.json()

    // Convert array to object for easy lookup
    // Expected API response: [{ track_id: "123", rating: 4 }, ...]
    const ratingsMap = {}
    if (Array.isArray(data)) {
      data.forEach(item => {
        ratingsMap[String(item.track_id)] = item.rating
      })
    }

    return ratingsMap
  } catch (error) {
    console.error('Error fetching user ratings:', error)
    return {}
  }
}

/**
 * Get rating for a specific track (checks API, falls back to localStorage)
 * @param {string|number} trackId - The track ID
 * @returns {Promise<number>} Rating (0-5) or 0 if not rated
 */
export async function getTrackRating (trackId) {
  if (!trackId) return 0

  if (!isSignedIn()) {
    // Not signed in, use localStorage
    return getLocalTrackRating(trackId)
  }

  // Try to get from localStorage first (instant)
  const localRating = getLocalTrackRating(trackId)

  // For now, return local rating immediately
  // In production, you might want to sync with API in background
  return localRating
}

/**
 * Rate a track (complete flow: save locally and submit to API)
 * @param {string|number} trackId - The track ID
 * @param {number} rating - Rating value (1-5)
 * @returns {Promise<Object>} Result object
 */
export async function rateTrack (trackId, rating) {
  const result = {
    savedLocally: false,
    submittedToAPI: false,
    data: null, // Add data field for API response
    errors: []
  }

  // Must be signed in
  if (!isSignedIn()) {
    result.errors.push('Must be signed in to rate tracks')
    return result
  }

  // Save locally first (instant feedback)
  const savedLocally = saveLocalTrackRating(trackId, rating)
  result.savedLocally = savedLocally

  if (!savedLocally) {
    result.errors.push('Failed to save rating locally')
  }

  // Submit to API
  try {
    const apiResult = await submitRatingToAPI(trackId, rating)
    result.submittedToAPI = apiResult.success
    result.data = apiResult.data // Store API response data

    if (!apiResult.success) {
      result.errors.push(apiResult.error || 'Failed to submit to API')
    }
  } catch (error) {
    result.errors.push('Exception during API submission: ' + error.message)
  }

  return result
}

/**
 * Check if user has rated a track
 * @param {string|number} trackId - The track ID
 * @returns {Promise<boolean>} True if user has rated this track
 */
export async function hasUserRated (trackId) {
  const rating = await getTrackRating(trackId)
  return rating > 0
}

/**
 * Get rating statistics for display
 * @param {string|number} trackId - The track ID
 * @param {number} averageRating - Average rating from API
 * @param {number} ratingCount - Number of ratings from API
 * @returns {Promise<Object>} Statistics object
 */
export async function getRatingStats (trackId, averageRating = 0, ratingCount = 0) {
  const userRating = await getTrackRating(trackId)

  return {
    userRating,
    averageRating,
    ratingCount,
    hasUserRated: userRating > 0,
    displayRating: userRating > 0 ? userRating : averageRating
  }
}

/**
 * Migrate localStorage ratings to Firebase after sign-in
 * @returns {Promise<Object>} Migration result
 */
export async function migrateLocalRatingsToFirebase () {
  if (!isSignedIn()) {
    return {
      success: false,
      message: 'User must be signed in to migrate ratings'
    }
  }

  const localRatings = getAllLocalRatings()
  const trackIds = Object.keys(localRatings)

  if (trackIds.length === 0) {
    return {
      success: true,
      message: 'No local ratings to migrate',
      migrated: 0
    }
  }

  console.log(`Migrating ${trackIds.length} local ratings to Firebase...`)

  let migrated = 0
  let failed = 0

  for (const trackId of trackIds) {
    const rating = localRatings[trackId]

    try {
      const result = await submitRatingToAPI(trackId, rating)
      if (result.success) {
        migrated++
      } else {
        failed++
      }
    } catch (error) {
      console.error(`Failed to migrate rating for track ${trackId}:`, error)
      failed++
    }
  }

  console.log(`✓ Migration complete: ${migrated} migrated, ${failed} failed`)

  return {
    success: true,
    message: `Migrated ${migrated} of ${trackIds.length} ratings`,
    migrated,
    failed,
    total: trackIds.length
  }
}

/**
 * Clear local ratings (after successful migration)
 * @returns {boolean} Success status
 */
export function clearLocalRatings () {
  try {
    localStorage.removeItem(RATINGS_KEY)
    return true
  } catch (error) {
    console.error('Error clearing local ratings:', error)
    return false
  }
}
